import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  List,
  Notification,
  IconEdit,
  Button,
  FeatureOriginPill,
} from 'shared/components';
import { fetchCustomFields, deleteLicenseCustomField } from './actions';
import { getLicenseCustomFields } from './helpers';
import { hasPermission } from 'shared/helpers';
import CustomFieldsForm from './CustomFieldsForm';
import './styles.scss';

const PAGE_SIZE = 20;

const CustomFields = ({ license }) => {
  const { t } = useTranslation();
  const canManageCustomFields = hasPermission('edit_license_custom_fields');
  const licenseID = get(license, 'id');
  const product = get(license, 'product');
  const [customFields, setCustomFields] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [hasLicenseCustomFields, setHasLicenseCustomFields] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentSort, setCurrentSort] = useState([{ desc: false, id: 'name' }]);

  const getCustomFields = useCallback(async () => {
    let data;
    try {
      data = await fetchCustomFields(licenseID);
      const fields = getLicenseCustomFields(product, get(data, 'data.results'));
      if (!fields) {
        throw Error("Couldn't get license custom fields");
      }
      setCustomFields(fields);
      setHasLicenseCustomFields(
        !!fields.find((f) => !!f.license_custom_field_id),
      );
      setLoading(false);
    } catch (err) {
      setLoading(false);
      Notification('error', t('There was an error while getting your data'));
    }
  }, [licenseID, product]);

  const resetCustomFieldToProductDefault = async (rowData) => {
    const customFieldID = get(rowData, 'original.license_custom_field_id');
    if (!customFieldID) {
      return false;
    }
    await deleteLicenseCustomField(customFieldID);
    await getCustomFields();
    return true;
  };

  useEffect(() => {
    getCustomFields();
  }, [getCustomFields]);

  const handleSortChange = (newSort) => setCurrentSort(newSort);

  const handleCustomFieldEditClick = (rowData) => {
    setSelectedField(rowData.original);
    setIsModalOpen(true);
  };

  const handleResetClick = (rowData) => {
    if (!canManageCustomFields) {
      Notification('error', t('Missing permission'), t('contact support'));
      return false;
    }
    resetCustomFieldToProductDefault(rowData);
    return true;
  };

  const columns = [
    { accessor: 'name', Header: t('Name') },
    {
      Cell: (rowData) => {
        // field.value when there is license custom field
        // field.default_value when there is product custom field
        const columnValue =
          rowData.original.value || rowData.original.default_value;
        const isLicenseCustom = !!rowData.original.license_custom_field_id;

        return (
          <div className="custom-field-value">
            <div style={{ minWidth: 150 }}>{columnValue}</div>
            <FeatureOriginPill type={isLicenseCustom ? 'license' : 'product'} />
          </div>
        );
      },
      Header: t('Value'),
    },
    ...(canManageCustomFields
      ? [
          {
            id: 'edit',
            Cell: (rowData) => (
              <Button
                className="edit-button"
                onClick={() => handleCustomFieldEditClick(rowData)}
                type="button"
              >
                <IconEdit height="16px" width="16px" />
              </Button>
            ),
            maxWidth: 50,
            sortable: false,
          },
        ]
      : []),
    ...(hasLicenseCustomFields && canManageCustomFields
      ? [
          {
            id: 'custom_field_default',
            Cell: (rowData) => {
              const isLicenseCustom =
                !!rowData.original.license_custom_field_id;
              return isLicenseCustom ? (
                <Button onClick={() => handleResetClick(rowData)} type="button">
                  {t('Reset to Product Default')}
                </Button>
              ) : (
                <></>
              );
            },
            maxWidth: 300,
            sortable: false,
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="CustomFields">
        <List
          columns={columns}
          data={customFields}
          minRows={2}
          pageSize={PAGE_SIZE}
          loading={isLoading}
          defaultSorted={currentSort}
          onSortedChange={handleSortChange}
          page={currentPage}
          onPageChange={(page) => setCurrentPage(page)}
          showPagination={customFields.length > PAGE_SIZE}
        />
      </div>
      {isModalOpen && selectedField && (
        <CustomFieldsForm
          closeCb={() => setIsModalOpen(false)}
          field={selectedField}
          license={license}
          refetchCustomFields={getCustomFields}
        />
      )}
    </>
  );
};

CustomFields.propTypes = {
  license: PropTypes.object.isRequired,
};

export default CustomFields;
